// The page's one floating tooltip — tooltip.js ported 1:1 in 05j-1. Two ways in, as before:
// - declarative: any element with `data-tooltip` shows it on hover (page-wide delegated
//   mouseover / mouseout; a tap toggles it on touch screens);
// - explicit: the canvas / SVG widgets that hit-test themselves (Shot / Pass Map, the
//   scatter, Radar's wedges…) call showTooltipAt / hideTooltip.
// A plain element + module functions, not a React context (05j fork 2A): the maps call it
// on every mousemove, and it measures itself right after its text changes — a React state
// would re-render per move and still need page-wide listeners for `data-tooltip`.
// The placement maths is tooltip-rules.ts (pure, tested).

import { tooltipAtPoint, tooltipOverElement } from "./tooltip-rules.js";

let tooltip: HTMLDivElement | null = null;
// The `data-tooltip` element it's showing (a tap on it again hides it); null for a
// showTooltipAt one.
let current: Element | null = null;

function tip(): HTMLDivElement {
  if (!tooltip) throw new Error("installTooltip() hasn't run");
  return tooltip;
}

function viewportWidth(): number {
  return document.documentElement.clientWidth;
}

function show(el: HTMLElement): void {
  const text = el.dataset.tooltip;
  if (!text) return;
  const t = tip();
  t.textContent = text;
  const pos = tooltipOverElement(el.getBoundingClientRect(), t.getBoundingClientRect(), viewportWidth());
  t.style.left = `${pos.left}px`;
  t.style.top = `${pos.top}px`;
  t.classList.toggle("tooltip-flipped", pos.flipped);
  t.classList.add("visible");
  current = el;
}

export function hideTooltip(): void {
  tip().classList.remove("visible");
  current = null;
}

/** Show `text` centred on a point (client px): above it, or below with `position: "below"`,
 *  flipping when there's no room. */
export function showTooltipAt(text: string, clientX: number, clientY: number, position?: string): void {
  const t = tip();
  t.textContent = text;
  t.classList.add("visible");
  const viewport = { width: viewportWidth(), height: document.documentElement.clientHeight };
  const pos = tooltipAtPoint(clientX, clientY, t.getBoundingClientRect(), viewport, position);
  t.style.left = `${pos.left}px`;
  t.style.top = `${pos.top}px`;
  current = null;
}

const tooltipTarget = (node: EventTarget | null) => (node instanceof Element ? node.closest<HTMLElement>("[data-tooltip]") : null);

/** Where tooltip.js loaded (after the snapshot, before the rest — App.tsx): the element goes
 *  on <body> and the listeners register at the same point in the page's order. */
export function installTooltip(): void {
  if (tooltip) return;
  tooltip = document.createElement("div");
  tooltip.id = "tooltip";
  document.body.appendChild(tooltip);

  document.addEventListener("mouseover", (e) => {
    // A drag of ours isn't a native browser drag, so the browser keeps firing mouseover on
    // whatever passes under the ghost (a Radar wedge…) — no tooltip for the whole drag.
    if (document.body.classList.contains("is-dragging-module") || document.body.classList.contains("is-moving-module")) return;
    const el = tooltipTarget(e.target);
    if (el) show(el);
  });

  document.addEventListener("mouseout", (e) => {
    const leaving = tooltipTarget(e.target);
    const entering = tooltipTarget(e.relatedTarget);
    if (leaving && !entering) hideTooltip();
  });

  // Touch: a tap toggles it, a tap anywhere else hides it.
  document.addEventListener(
    "touchstart",
    (e) => {
      const el = tooltipTarget(e.target);
      if (el) {
        e.preventDefault();
        if (current === el) hideTooltip();
        else show(el);
      } else {
        hideTooltip();
      }
    },
    { passive: false },
  );

  window.addEventListener("scroll", hideTooltip, { passive: true });
}
